import { useToast } from '@chakra-ui/react';
import { useMutation, useQuery } from '@tanstack/react-query';
import React, { ReactNode } from 'react';
import { ChakraComponents as CC } from '@Modules/core/lib/chakra';
import CustomReactTable from '@Modules/core/components/table/CustomReactTable';
import CustomDrawer from '@Modules/core/components/CustomDrawer';
import { getCardAccountLinkDataTableColumns, TAction, TCardAccountLinkDataTableData } from './CardAccountLinkDataTableMeta';
import CustomFieldSet from '@Modules/core/components/CustomFieldSet';
import CustomAppModal from '@Modules/core/components/modal/CustomModal';
import CustomSaveBtn from '@Modules/core/components/form/CustomSaveBtn';
import GenericFormUI from '@Shared/components/form/GenericFormUI';
import cardAccountLinkDataTableRowFormMeta from '../../../formHandlers/debitOperations/cardAccountLink/CardAccountLinkDataTableRowFormMeta';
import cardAccountLinkFilterFormMeta from '../../../formHandlers/debitOperations/cardAccountLink/CardAccountLinkFilterFormMeta';
import CustomFilterBtn from '@Modules/core/components/form/CustomFilterBtn';

const initialColumnVisibility: {[x: string]: boolean} = {};

const CardAccountLinkMixedHandler = () => {
    const [rowDataTable, setRowDataTable] =
      React.useState<TCardAccountLinkDataTableData>({
        primaryKey: '',
        bankAccount: "",
        bankAccountType: "",
        chequeBook: "",
        pan: {
          pan1: "",
          pan2: "",
          pan3: "",
          pan4: "",
          pan5: "",
        },
        customer: {
          name: "",
          unknown: "",
        },
      });

    const editModalRef = React.useRef<any>(null);

    const formModalRef = React.useRef<{
      submitBtnRef: React.MutableRefObject<any>,
    }>(null);

    const detailsDrawerRef = React.useRef<any>(null);

    const toast = useToast();

  //   Initialize react Query to GET Data
  const queryKey = ['CardAccountLink'];
  const { data, isLoading } = useQuery({
    queryKey: queryKey,
    queryFn: async () => {
      // TODO: Replace this by your fetcher function
      return new Promise(function (resolve) {
        setTimeout(() => resolve('data'), 500);
      });
    },
    onSuccess: (data_: any) => {
      // console.log(data_);
    },
    onError: (data_: any) => {
      toast({
        position: 'top-right',
        title:
          "Une erreur inattendue s'est produite lors de la récupération des données",
        isClosable: true,
        status: 'error',
      });
    },
  });

  const { isLoading: isSubmitting } = useMutation(async () => {}, {
    onSuccess: (data) => {
      console.log('Response: ', data);
      toast({
        position: 'top-right',
        title: 'Sauvegarde effectuée avec succès',
        isClosable: true,
        status: 'success',
      });
    },
    onError: (error) => {
      toast({
        position: 'top-right',
        title:
          "Une erreur inattendue s'est produite lors de l'enregistrement des données",
        isClosable: true,
        status: 'error',
      });
      console.log('Error: ', error);
    },
  });

  const onSubmit = async (payload: any) => {
    console.log('payload: ', payload);
    // TODO: Uncomment the following lines to put the logic of backend feedback operation
    /*try {
            await mutateAsync();
        } catch (error) {
            console.log(error);
        }*/
  };

  const handleDataTableBtn = (row: any, action: TAction) => {
    setRowDataTable(row.original);
    if(action === "edit") editModalRef?.current?.onOpen();
    else detailsDrawerRef?.current?.onOpen();

  };

    const columns = getCardAccountLinkDataTableColumns({
      isLoading,
      handleDataTableBtn,
    });

    const triggerSubmit = () => {
      formModalRef?.current?.submitBtnRef?.current?.click();
    };
  
    const onModalFormSubmit = async (payload: any) => {
      console.log('Modal payload: ', payload);
    };

    const onFilterFormSubmit = async (payload: any) => {
      console.log('Filter payload: ', payload);
    };
  
    const drawerContent = React.useMemo(() => (<>
      <CC.SimpleGrid columns={3} spacing={5}>
      
        {columns.map((column, i) => column.enableHiding !== false && column.id !== 'action' && column.header && <CC.Box key={i}>
          <CC.Heading size='xs' textTransform='uppercase'>
          {column.header as ReactNode}
          </CC.Heading>
          <CC.Text pt='2' fontSize='sm'>
          {rowDataTable[column.id!]}
          </CC.Text>
        </CC.Box>)}
      </CC.SimpleGrid>
      <CC.Divider my={5} />
      
      <CustomFieldSet title='PAN'>
        <CC.SimpleGrid columns={2} spacing={5}>
          {
            rowDataTable["pan"] && Object.keys(rowDataTable["pan"]).map((panKey) => {
              return (<CC.Text pt='2' fontSize='sm' key={panKey}>
                {rowDataTable.pan[panKey]}
              </CC.Text>)
            })
          }
        </CC.SimpleGrid>
      </CustomFieldSet>

      <CustomFieldSet title='CLIENT'>
        <CC.SimpleGrid columns={2} spacing={5}>
          {
            rowDataTable["customer"] && Object.keys(rowDataTable["customer"]).map((customerKey) => {
              return (<CC.Text pt='2' fontSize='sm' key={customerKey}>
                {rowDataTable.customer[customerKey]}
              </CC.Text>)
            })
          }
        </CC.SimpleGrid>
      </CustomFieldSet>
    </>),
    [columns, rowDataTable]);

    return (
        <>
            <GenericFormUI
                meta={cardAccountLinkFilterFormMeta}
                onSubmit={onFilterFormSubmit}
                isLoading={isLoading}
                isSubmitting={isSubmitting}
                submitBtn={<CC.HStack my={4} justifyContent={'flex-end'}>
                <CustomFilterBtn isLoading={isLoading} isSubmitting={false} />
              </CC.HStack>}
            />
            <CustomReactTable data={data || [{}, {}, {}]} columns={columns} initialColumnVisibility={initialColumnVisibility} />
            <CustomAppModal
              title="Editer"
              ref={editModalRef}
              actionBtn={
                <CustomSaveBtn
                  isLoading={isLoading}
                  isSubmitting={isSubmitting}
                  onClick={triggerSubmit}
                />
              }
              actionBtnText={'Valider'}
              size="3xl"
              isCentered
              closeOnOverlayClick={false}
            >
              <GenericFormUI
                data={rowDataTable}
                meta={cardAccountLinkDataTableRowFormMeta}
                onSubmit={onModalFormSubmit}
                isLoading={isLoading}
                isSubmitting={isSubmitting}
                ref={formModalRef}
              />
            </CustomAppModal>
            <CustomDrawer title='Détails' ref={detailsDrawerRef}>
            {drawerContent}
            </CustomDrawer>
        </>
    );
};

export default CardAccountLinkMixedHandler;
